import isNil from 'lodash/isNil'
import isEmpty from 'lodash/isEmpty'
import { isTimeLayer } from './timeLayers/timeline'

export const ACTIVE_LAYERS_SETTING_ID = 1
export const FAVORITE_LAYERS_SETTING_ID = 2

// Fallback basemap used when a user ends up with no valid active layers at all.
export const DEFAULT_BASEMAP_ID = 81

/**
 * Decide which saved layer ids are still valid against the layer catalog.
 *
 * WEB-7030: the result of this reconciliation is PUT to the server, and mobile syncs that
 * value down. So "I can't see the catalog" must never be conflated with "none of the
 * user's layers are valid" — that mistake silently deleted every saved Active and Favorite
 * layer across all of a user's devices. When the catalog or the settings are not loaded,
 * this returns skip:true and callers must not write anything.
 *
 * @returns {{skip: boolean, active: number[], favorite: number[],
 *            activeChanged: boolean, favoriteChanged: boolean}}
 */
export const reconcileLayerSettings = ({ userSettings, layersDictionary }) => {
  const skipped = { skip: true, active: [], favorite: [], activeChanged: false, favoriteChanged: false }

  // An empty dictionary means the layer fetch never landed, not that everything is stale.
  if (isNil(layersDictionary) || isEmpty(layersDictionary)) return skipped
  if (isNil(userSettings) || !Array.isArray(userSettings) || userSettings.length === 0) return skipped

  const savedActive = userSettings.find(s => s.settingId === ACTIVE_LAYERS_SETTING_ID)
  const savedFavorite = userSettings.find(s => s.settingId === FAVORITE_LAYERS_SETTING_ID)

  // Without a readable active-layers setting there is nothing to validate against.
  if (!savedActive || !Array.isArray(savedActive.valueArray)) return skipped

  const exists = id => !isNil(layersDictionary[id])

  // One weather layer at a time (weather time bar, matched with iOS and Android): layers
  // saved before the rule, or synced from an older client, keep only the top one.
  const weather = savedActive.valueArray.filter(id => exists(id) && isTimeLayer(layersDictionary[id]))
  const keepWeather = weather.length > 1
    ? weather.reduce((a, b) => ((layersDictionary[b].drawOrder || 0) > (layersDictionary[a].drawOrder || 0) ? b : a))
    : null
  const validActive = savedActive.valueArray.filter(id => exists(id) && (keepWeather === null || !weather.includes(id) || id === keepWeather))
  const active = validActive.length > 0 ? validActive : [DEFAULT_BASEMAP_ID]

  const hasFavorite = Array.isArray(savedFavorite?.valueArray)
  const favorite = hasFavorite ? savedFavorite.valueArray.filter(exists) : []

  return {
    skip: false,
    active,
    favorite,
    activeChanged: JSON.stringify(savedActive.valueArray) !== JSON.stringify(active),
    favoriteChanged: hasFavorite && savedFavorite.valueArray.length !== favorite.length
  }
}
